import React, { useEffect, useState } from 'react';
import Parse from '../../parseConfig';
import '../styles/SideBarLeft.css';
import '../styles/SideBarToggle.css';
import '../styles/phcolours.css';

import useUIPresentationMode from '../helpers/useUIPresentationMode';
import CollapseIconHover from '../../images/IconLeftArrowBlue@3x.png';
import CollapseIcon from '../../images/IconLeftArrowGrey@3x.png';
import IconLayers from '../../images/IconLayers@3x.png';
import IconLayersActive from '../../images/IconLayersActive@3x.png';
import FraemohsLogo from '../../images/FraemohsSymbol.png';
import {
  BUILT_HOME_MAP_REVIEW_FILTERS,
  BUILT_HOME_TYPE_FILTERS,
  BUILD_STYLE_FILTERS,
} from './builtProjectUtils';

export default function BuiltSideBarLeft({
  isVisible = true,
  onRequestClose = () => {},
  showAdminButton = false,
  onOpenAdmin = () => {},
  onLogout = () => {},
  filters = new Set(),
  setFilters = () => {},
  buildStyleOptions = BUILD_STYLE_FILTERS,
  typeFilters = new Set(),
  setTypeFilters = () => {},
  typeOptions = BUILT_HOME_TYPE_FILTERS,
  basePlanFilters = null,
  setBasePlanFilters = () => {},
  basePlanOptions = [],
  reviewFilters = new Set(),
  setReviewFilters = () => {},
  hasNeedsReview = false,
  consultantFilters = null,
  setConsultantFilters = () => {},
  consultantOptions = [],
  onClearAndZoom = () => {},
  layers = { primary: [], secondary: [] },
  selectedBaseMap = 'None',
  setSelectedBaseMap = () => {},
  isParcelBoundariesVisible = false,
  toggleParcelBoundaries = () => {},
}) {
  const [isCollapseButtonHovering, setIsCollapseButtonHovering] = useState(false);
  const [userName, setUserName] = useState('');
  const [layersExpanded, setLayersExpanded] = useState(false);
  const [basePlansExpanded, setBasePlansExpanded] = useState(true);
  const [consultantsExpanded, setConsultantsExpanded] = useState(true);
  const { presentationModeSize } = useUIPresentationMode();
  const hideLogo = presentationModeSize === 5;
  const buildStyles = buildStyleOptions;
  const homeTypes = typeOptions;
  const reviewOptions = BUILT_HOME_MAP_REVIEW_FILTERS;
  const showClear = filters.size !== buildStyles.length
    || typeFilters.size !== homeTypes.length
    || basePlanFilters !== null
    || (hasNeedsReview && reviewFilters.size !== reviewOptions.length)
    || consultantFilters !== null;
  const selectedBasePlanValues = basePlanFilters || new Set(basePlanOptions.map((option) => option.value));

  useEffect(() => {
    const currentUser = Parse.User.current();
    if (currentUser) {
      const fullName = `${currentUser.get('firstName')} ${currentUser.get('lastName')}`;
      setUserName(fullName.trim());
    }
  }, []);

  const renderCheckboxGroup = (options, selectedValues, onChange) => options.map((option) => {
    const { value, label } = typeof option === 'string'
      ? { value: option, label: option }
      : option;
    return (
    <label key={value} className="checkbox" style={{ margin: '2px 0 0 10px', padding: 0 }}>
      <input type="checkbox" checked={selectedValues.has(value)} onChange={() => onChange(value)} />
      <span>{label}</span>
    </label>
    );
  });

  return (
    <div
      className="sidebar"
      style={{
        width: '100%',
        flex: 1,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderRight: isVisible ? '0.75px solid #596A99' : 'none',
        transition: 'flex-basis 0.3s ease, opacity 0.3s ease',
        opacity: isVisible ? 1 : 0,
      }}
    >
      <button
        className="sidebar-toggle-button sidebar-left-toggle-button-expanded"
        onClick={onRequestClose}
        onMouseEnter={() => setIsCollapseButtonHovering(true)}
        onMouseLeave={() => setIsCollapseButtonHovering(false)}
      >
        <img src={isCollapseButtonHovering ? CollapseIconHover : CollapseIcon} alt="Hide Sidebar" />
      </button>

      <div className="sidebar-inner">
        {!hideLogo && (
          <div className="sidebar-logo-container">
            <img
              src={FraemohsLogo}
              alt="Fraemohs Logo"
              className="sidebar-logo"
              style={{ width: 82, height: 82, objectFit: 'contain', marginTop: 5, marginBottom: 5 }}
            />
          </div>
        )}

        <div
          className="sidebar-heading-row"
          onClick={() => setLayersExpanded((current) => !current)}
          title="Toggle Layers Section"
          style={{ cursor: 'pointer' }}
        >
          <img
            src={layersExpanded ? IconLayersActive : IconLayers}
            alt="Layers Icon"
            className="sidebar-icon-left"
          />
          <span className={`sidebar-heading-label ${layersExpanded ? 'active' : 'inactive'}`}>
            Layers
          </span>
        </div>

        <div className={`collapsible-section ${layersExpanded ? 'expanded' : ''}`}>
          <label className="checkbox">
            <input
              type="checkbox"
              checked={isParcelBoundariesVisible}
              onChange={(event) => toggleParcelBoundaries(event.target.checked)}
            />
            <span>Parcel Boundaries</span>
          </label>

          {Array.isArray(layers.secondary) && layers.secondary.map((layer) => (
            <label className="radio" key={layer.id}>
              <input
                type="radio"
                name="built-basemap"
                value={layer.get('name')}
                checked={selectedBaseMap === layer.get('name')}
                onChange={(event) => setSelectedBaseMap(event.target.value)}
              />
              <span>{layer.get('name')}</span>
            </label>
          ))}

          <label className="radio">
            <input
              type="radio"
              name="built-basemap"
              value="None"
              checked={selectedBaseMap === 'None'}
              onChange={(event) => setSelectedBaseMap(event.target.value)}
            />
            <span>Road Map</span>
          </label>
        </div>

        <div className="sidebar-heading-row" style={{ marginTop: 12 }}>
          <span className="sidebar-heading-label active">Filter</span>
        </div>

        <div style={{ marginTop: 12 }}>
          {hasNeedsReview && (
            <>
              <div className="built-filter-group-title">Review Status</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {reviewOptions.map(({ value, label }) => (
                  <label key={value} className="checkbox" style={{ margin: '2px 0 0 10px', padding: 0 }}>
                    <input
                      type="checkbox"
                      checked={reviewFilters.has(value)}
                      onChange={() => {
                        const next = new Set(reviewFilters);
                        next.has(value) ? next.delete(value) : next.add(value);
                        setReviewFilters(next);
                      }}
                    />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
            </>
          )}

          <div className="built-filter-group-title">Build Style</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {renderCheckboxGroup(buildStyles, filters, (buildStyle) => {
              const next = new Set(filters);
              next.has(buildStyle) ? next.delete(buildStyle) : next.add(buildStyle);
              setFilters(next);
            })}
          </div>

          <div className="built-filter-group-title">Type</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {renderCheckboxGroup(homeTypes, typeFilters, (type) => {
              const next = new Set(typeFilters);
              next.has(type) ? next.delete(type) : next.add(type);
              setTypeFilters(next);
            })}
          </div>

          {basePlanOptions.length > 0 && (
            <>
              <div
                className="built-filter-group-title built-filter-group-toggle"
                role="button"
                tabIndex={0}
                aria-expanded={basePlansExpanded}
                onClick={() => setBasePlansExpanded((current) => !current)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setBasePlansExpanded((current) => !current);
                  }
                }}
              >
                Base Plan <span className="built-filter-group-arrow" aria-hidden="true">{basePlansExpanded ? '▾' : '▸'}</span>
              </div>
              {basePlansExpanded && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {basePlanOptions.map(({ value, label }) => (
                    <label key={value} className="checkbox" style={{ margin: '2px 0 0 10px', padding: 0 }}>
                      <input
                        type="checkbox"
                        checked={selectedBasePlanValues.has(value)}
                        onChange={() => {
                          const allValues = basePlanOptions.map((option) => option.value);
                          const next = new Set(basePlanFilters || allValues);
                          next.has(value) ? next.delete(value) : next.add(value);
                          setBasePlanFilters(next.size === allValues.length ? null : next);
                        }}
                      />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>
              )}
            </>
          )}

          {consultantOptions.length > 0 && (
            <>
              <div
                className="built-filter-group-title built-filter-group-toggle"
                role="button"
                tabIndex={0}
                aria-expanded={consultantsExpanded}
                onClick={() => setConsultantsExpanded((current) => !current)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setConsultantsExpanded((current) => !current);
                  }
                }}
              >
                Sales Consultant <span className="built-filter-group-arrow" aria-hidden="true">{consultantsExpanded ? '▾' : '▸'}</span>
              </div>
              {consultantsExpanded && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {renderCheckboxGroup(
                    consultantOptions,
                    consultantFilters || new Set(consultantOptions.map((option) => option.value)),
                    (consultant) => {
                    const allValues = consultantOptions.map((option) => option.value);
                    const next = new Set(consultantFilters || allValues);
                    next.has(consultant) ? next.delete(consultant) : next.add(consultant);
                    setConsultantFilters(next.size === allValues.length ? null : next);
                  })}
                </div>
              )}
            </>
          )}
        </div>

        {showClear && (
          <div
            className="sidebarRight-landinfo-row"
            onClick={() => {
              setFilters(new Set(buildStyles));
              setTypeFilters(new Set(homeTypes));
              setBasePlanFilters(null);
              setReviewFilters(new Set(reviewOptions.map(({ value }) => value)));
              setConsultantFilters(null);
              onClearAndZoom();
            }}
            style={{
              marginTop: 35,
              justifyContent: 'center',
              textAlign: 'center',
              cursor: 'pointer',
              backgroundColor: 'var(--phwashedorangebackground)',
            }}
            onMouseEnter={(event) => {
              event.currentTarget.style.backgroundColor = 'var(--phorangefont)';
              event.currentTarget.querySelector('span').style.color = '#fff';
            }}
            onMouseLeave={(event) => {
              event.currentTarget.style.backgroundColor = 'var(--phwashedorangebackground)';
              event.currentTarget.querySelector('span').style.color = 'var(--phorangefont)';
            }}
          >
            <span style={{ color: 'var(--phorangefont)', fontWeight: 500 }}>Clear filters</span>
          </div>
        )}
      </div>

      <div className="sidebar-footer built-sidebar-footer">
        {showAdminButton && (
          <button type="button" className="stock-listing-admin-button" onClick={onOpenAdmin}>
            Admin
          </button>
        )}

        {userName && (
          <div className="logout-button" style={{ cursor: 'default', pointerEvents: 'none' }}>
            {userName}
          </div>
        )}

        <button className="logout-button" onClick={onLogout}>
          Log Out
        </button>

        <div className="copyright">© Fraemohs 2026</div>
      </div>
    </div>
  );
}
